import { get, repeat } from 'lodash/fp'
import styled from 'styled-components'

import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useExperimentState } from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import { useRouter } from 'next/router'
import { getDiscountPercentage } from 'productTemplate/utils/under-retail-utils'
import { formatPrice } from 'shared/components/ProductGrid/__utils__/formatPrice'
import GridItem from 'shared/components/ProductGrid/GridItem'
import { usePageContext } from 'shared/contexts/PageContextProvider'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import SpecificPageTrackingEvents from 'shared/enums/SpecificPageTrackingEvents'
import useCurrency from 'shared/hooks/useCurrency'
import { useWindowSize } from 'shared/hooks/useWindowSize'
import { fonts, media } from 'shared/lib'
import { sizes } from 'shared/lib/media'
import IProduct from 'shared/types/IProduct'

const TP = 'shared.components.ProductGrid.ProductGrid'

type ProductGridProps = {
  dataTrackingEvent?: (...args: any) => void
  ensureNoWidow?: boolean
  hideAdditionalPrices?: boolean
  hidePrices?: boolean
  maxColumns?: number
  products: IProduct[]
}

export const ProductGrid = ({
  dataTrackingEvent,
  ensureNoWidow = false,
  hideAdditionalPrices = false,
  hidePrices = false,
  maxColumns = 4,
  products,
}: ProductGridProps) => {
  const mobilePLPOptimizationExperiment: EXPERIMENT_NAME = 'web_fc_mobile_plp_optimization'
  const enableExperiments = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const isHidePlusSignInGridPricesEnabled = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_HIDE_PLUS_SIGN_GRID_PRICES,
  )
  const DEFAULT_SELECTED_CURRENCY_ISO = 'USD'
  const DEFAULT_SELECTED_CURRENCY_SYMBOL = '$'
  const { availableCurrencies, currencyCode } = useShoppingRegionContext()
  const { selectedCurrency } = useCurrency()
  const experimentState = useExperimentState()
  const isoCode = selectedCurrency?.isoCode || DEFAULT_SELECTED_CURRENCY_ISO
  const displaySymbol =
    availableCurrencies?.find((selectedCurrency) => selectedCurrency.isoCode === currencyCode)
      ?.symbol || DEFAULT_SELECTED_CURRENCY_SYMBOL
  const pageContext = usePageContext()
  const router = useRouter()
  const { windowSize } = useWindowSize()
  const isMobile = windowSize.width != undefined && windowSize.width < sizes.large
  const urlParams = router.query
  const isSearch = 'query' in urlParams
  const isPLP =
    isSearch ||
    pageContext.value === SpecificPageTrackingEvents.FilteredGridPage ||
    pageContext.value === SpecificPageTrackingEvents.CollectionPage

  const displayMobilePLPOptimization =
    enableExperiments &&
    isMobile &&
    isPLP &&
    experimentState[mobilePLPOptimizationExperiment]?.groupAssigned === 'variant'

  return !!products.length ? (
    <Grid
      maxColumns={maxColumns}
      ensureNoWidow={ensureNoWidow}
      isOptimizedPLP={displayMobilePLPOptimization}
    >
      {products.map(({ brandName, id, name, pictureUrl, retailPrice, price, slug }, i) => {
        const lowestLocalizedPrice = get('localizedValue')(price)
        const retailLocalizedPrice = get('localizedValue')(retailPrice)
        const additionalPriceSymbol =
          hideAdditionalPrices || isHidePlusSignInGridPricesEnabled ? '' : '+'
        const discountPercentage = getDiscountPercentage(retailLocalizedPrice, lowestLocalizedPrice)

        return (
          <GridItem
            brand={brandName}
            currency={isoCode}
            dataTrackingEvent={dataTrackingEvent}
            hidePrice={hidePrices}
            imageUrl={pictureUrl}
            index={i}
            key={id}
            name={name}
            price={formatPrice(lowestLocalizedPrice, isoCode, displaySymbol, additionalPriceSymbol)}
            productUrl={slug}
            discountPercentage={discountPercentage || 0}
            isOptimizedPLP={displayMobilePLPOptimization}
          />
        )
      })}
    </Grid>
  ) : (
    <NoResults data-testid="productGridNoResults">
      <Title>{t(`${TP}.noResTitle`, 'No Results Found')}</Title>
      <SubTitle>
        {t(`${TP}.noResultDesc`, 'Please check your filters or search for another sneaker')}
      </SubTitle>
    </NoResults>
  )
}

export default ProductGrid

const Grid = styled.div<{ maxColumns: number; ensureNoWidow: boolean; isOptimizedPLP: boolean }>`
  display: grid;
  grid-gap: ${({ isOptimizedPLP }) => (isOptimizedPLP ? '2px' : '10px')};
  grid-template-columns: minmax(150px, 1fr);
  ${media.extraSmall`
    grid-template-columns: minmax(150px, 1fr) minmax(150px, 1fr);
  `}
  ${media.medium`
    grid-gap: ${({ isOptimizedPLP }) => (isOptimizedPLP ? '2px' : '20px')};
    grid-template-columns: minmax(226px, 1fr) minmax(226px, 1fr) minmax(226px, 1fr);
    ${({ ensureNoWidow }: { ensureNoWidow: boolean }) =>
      ensureNoWidow &&
      `a:last-child:nth-child(3n-2) {
        display: none;
      }
    `}
  `}
  ${media.large`
    ${({ maxColumns }: { maxColumns: number }) =>
      `grid-template-columns:${repeat(maxColumns)(' 1fr')};`}
    ${({ ensureNoWidow }: { ensureNoWidow: boolean }) =>
      ensureNoWidow &&
      `a:last-child:nth-child(3n-2) {
        display: initial;
      }
    `}
  `}
`

const NoResults = styled.div`
  height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${media.large`
    height: 600px;
  `}
`

const Title = styled.span`
  ${fonts.SUBTITLE_2}
  margin-top: 40px;
  margin-bottom: 10px;
`

const SubTitle = styled.span`
  ${fonts.BODY_TEXT}
`
